import { useState } from 'react';
import useSWR from 'swr';
import { get } from '@/lib/api';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { SampleOrderDialog } from '@/components/domain/SampleOrderDialog';
import type { AnyInventoryItem } from '@/lib/ordering';
import { cn } from '@/lib/cn';

type InventoryPayload = { success: boolean; count: number; items: AnyInventoryItem[] };
type HistoryPayload = { success: boolean; orders: Array<Record<string, any>> };

// Samples & Blanks — masterInventory base products with sample ordering +
// sampleOrders history. Port of admin-dashboard's Samples tab.
export default function Samples() {
  const [q, setQ] = useState('');
  const [view, setView] = useState<'products' | 'history'>('products');
  const [orderItem, setOrderItem] = useState<AnyInventoryItem | null>(null);
  const { data: inv, isLoading: invLoading, mutate: mutateInv } = useSWR<InventoryPayload>('/api/master-inventory', get);
  const { data: hist, mutate: mutateHist } = useSWR<HistoryPayload>('/api/sample-orders', get);

  const items = inv?.items || [];
  const history = hist?.orders || [];

  const filtered = items.filter((item) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      String(item.styleNumber || '').toLowerCase().includes(needle) ||
      String(item.color || '').toLowerCase().includes(needle) ||
      String(item.brand || '').toLowerCase().includes(needle) ||
      String(item.category || '').toLowerCase().includes(needle) ||
      String(item.id || '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Samples & Blanks</h1>
      <p className="mb-3 text-[12px] text-muted">
        Order blank products or samples for reps · {items.length} base products · {history.length} sample orders
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by style, color, brand…" className="max-w-[280px]" />
        <div className="flex gap-1.5">
          {(['products', 'history'] as const).map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={cn('rounded-sm px-2.5 py-1 text-[11px]', view === v ? 'bg-primary text-white' : 'border border-border bg-surface text-text/75 hover:bg-bg')}
            >
              {v === 'products' ? 'Products' : 'Order History'}
            </button>
          ))}
        </div>
        <Button variant="secondary" size="sm" className="ml-auto" onClick={() => { void mutateInv(); void mutateHist(); }}>Refresh</Button>
      </div>

      <div className="rounded-md bg-surface shadow-card">
        {view === 'products' ? (
          invLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-6 w-full" />
            </div>
          ) : filtered.length ? (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                  <th className="px-3 py-2 font-semibold">Product</th>
                  <th className="px-3 py-2 font-semibold">Style</th>
                  <th className="px-3 py-2 font-semibold">Color</th>
                  <th className="px-3 py-2 font-semibold">Brand</th>
                  <th className="px-3 py-2 font-semibold">Category</th>
                  <th className="px-3 py-2 font-semibold" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => {
                  const variantCount = item.variants ? Object.keys(item.variants).length : 0;
                  return (
                    <tr key={item.id} className="border-b border-border/50 hover:bg-bg/50">
                      <td className="px-3 py-2">
                        <div className="font-medium">{[item.brand, item.color, item.category].filter(Boolean).join(' ') || item.id}</div>
                        <div className="font-mono text-[10px] text-subtle">Shopify {item.shopifyProductId || 'N/A'} · {variantCount} sizes</div>
                      </td>
                      <td className="px-3 py-2"><span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[11px] text-primary">{item.styleNumber || '-'}</span></td>
                      <td className="px-3 py-2">{item.color || '-'}</td>
                      <td className="px-3 py-2">{item.brand || '-'}</td>
                      <td className="px-3 py-2">{item.category || '-'}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" onClick={() => setOrderItem(item)}>Order</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-[12px] text-muted">
              {q ? 'No matching products.' : 'No base products found — create them in Prize Products / Product Config first.'}
            </div>
          )
        ) : history.length ? (
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-border text-left text-[10.5px] uppercase tracking-wide text-subtle">
                <th className="px-3 py-2 font-semibold">Product</th>
                <th className="px-3 py-2 font-semibold">Units</th>
                <th className="px-3 py-2 font-semibold">Ordered by</th>
                <th className="px-3 py-2 font-semibold">Ship to</th>
                <th className="px-3 py-2 font-semibold">Date</th>
                <th className="px-3 py-2 font-semibold">Order #</th>
              </tr>
            </thead>
            <tbody>
              {history.map((o) => (
                <tr key={o.id} className="border-b border-border/50 hover:bg-bg/50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{o.productName || '-'}</div>
                    <div className="font-mono text-[10px] text-subtle">{o.masterInventoryId || ''}</div>
                  </td>
                  <td className="px-3 py-2">{o.totalUnits || 0}</td>
                  <td className="px-3 py-2">{o.orderedBy || '-'}</td>
                  <td className="px-3 py-2">{o.shipTo ? [o.shipTo.firstName, o.shipTo.lastName].filter(Boolean).join(' ') : '-'}</td>
                  <td className="px-3 py-2">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : '-'}</td>
                  <td className="px-3 py-2">
                    {o.shopifyOrderNumber
                      ? <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 font-mono text-[11px] text-primary">{o.shopifyOrderNumber}</span>
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-[12px] text-muted">No sample orders yet — they appear here once placed.</div>
        )}
      </div>

      <SampleOrderDialog
        item={orderItem}
        open={!!orderItem}
        onClose={() => setOrderItem(null)}
        onPlaced={() => void mutateHist()}
      />
    </div>
  );
}
